import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { EnrollmentWithDetails } from '../types/program';
import { useParams, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { PrintableInvoice } from '../components/PrintableInvoice';
import { InvoiceDetailsBody, Invoice, InvoiceWithDetails, LineItem } from '../components/InvoiceDetailsBody';
import { analytics, getSessionId } from '../utils/analytics';
import { checkoutPath } from '../utils/checkoutSource';
import { useAuth } from '../contexts/AuthContext';
import { useLabels } from '../config/useLabels';
import { useLandingBrand } from '../hooks/useLandingBrand';

export function InvoiceDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const t = useLabels();
  const [showPrintView, setShowPrintView] = useState(false);

  const { data: invoice, isLoading, error } = useQuery<InvoiceWithDetails | null>({
    queryKey: ['invoice-details', id],
    queryFn: async () => {
      try {
        const { data, error } = await supabase
          .from('invoices')
          .select(`
            id,
            enrollment_id,
            total_amount,
            amount_paid,
            status,
            due_date,
            period_start,
            period_end,
            is_prorated,
            created_at,
            updated_at,
            payments (
              id,
              amount,
              payment_method,
              payment_date,
              status,
              bank_account_setup_intent_id,
              bank_account_setup_intents (
                id,
                bank_name,
                account_type,
                last4
              )
            ),
            enrollments!inner (
              id,
              student_id,
              program_id,
              created_at,
              updated_at,
              metadata,
              students!inner (
                first_name,
                last_name,
                date_of_birth
              ),
              programs!inner (
                id,
                name,
                type,
                price,
                billing_type,
                brand_key,
                active,
                start_date,
                end_date,
                schedule_cron,
                created_at,
                updated_at
              )
            )
          `)
          .eq('id', id)
          .maybeSingle();

        if (error) throw error;
        if (!data) return null;
        // Source of truth: map metadata to selected_days for consumers
        type EnrollShape = { metadata?: Record<string, unknown>; [k: string]: unknown }
        const row = data as unknown as { enrollments: EnrollShape }
        const enroll = row.enrollments
        const md = (enroll?.metadata as Record<string, unknown> | undefined) || {}
        const selectedDays = Array.isArray((md as Record<string, unknown>).selected_days)
          ? (md as Record<string, unknown>).selected_days as string[]
          : []
        ;(enroll as EnrollShape & { selected_days?: string[] }).selected_days = selectedDays
        return row as unknown as InvoiceWithDetails
      } catch (err) {
        console.error('Error fetching invoice:', err);
        throw err;
      }
    }
  });

  // An invoice email is the commonest way into the portal. Open it on the brand that sold the
  // class, so a Kaleido invoice is not read under the BHCS profile.
  useLandingBrand(invoice?.enrollments?.programs?.brand_key);

  // Track invoice view when invoice is loaded
  useEffect(() => {
    if (invoice && user && id) {
      analytics.trackInvoiceView({
        user,
        invoiceId: id,
        sessionId: getSessionId(),
        metadata: {
          student_name: `${invoice.enrollments.students.first_name} ${invoice.enrollments.students.last_name}`,
          program_name: invoice.enrollments.programs.name,
          invoice_status: invoice.status,
          total_amount: invoice.total_amount,
          amount_paid: invoice.amount_paid,
          balance_due: invoice.total_amount - invoice.amount_paid
        }
      });
    }
  }, [invoice, user, id]);

  // Fetch invoice line items with pricing information
  const { data: lineItems, isLoading: isLineItemsLoading } = useQuery<LineItem[]>({
    queryKey: ['invoice-line-items', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('invoicing_line_items')
        .select(`
          id, 
          description, 
          amount, 
          line_item_config_id,
          line_item_configs (
            name,
            type,
            subtype
          )
        `)
        .eq('invoice_id', id)
        .order('created_at', { ascending: true });
      if (error) throw error;
      return data as LineItem[];
    },
    enabled: !!id
  });

  // Deprecated local helper replaced by shared getProgramTypeIcon

  if (isLoading) {
    return <div data-testid="loading-skeleton">Loading invoice details...</div>;
  }

  if (error) {
    return (
      <div className="p-3 sm:p-4">
        <h1 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-6">Invoice Details</h1>
        <div className="bg-white p-6 rounded-lg shadow">
          <p className="text-sm sm:text-base text-red-600">You don't have permission to view this invoice</p>
        </div>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="p-3 sm:p-4">
        <h1 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-6">Invoice Details</h1>
        <div className="bg-white p-6 rounded-lg shadow">
          <p className="text-sm sm:text-base text-gray-900 font-medium">Invoice not found</p>
          <p className="text-sm sm:text-base text-gray-600 mt-2">
            This invoice may have been deleted, or the link you used is invalid.
          </p>
          <div className="mt-4">
            <button
              onClick={() => navigate('/payments')}
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
            >
              Back to Payments
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!invoice.enrollments) {
    return (
      <div className="p-3 sm:p-4">
        <h1 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-6">Invoice Details</h1>
        <div className="bg-white p-6 rounded-lg shadow">
          <p className="text-sm sm:text-base text-red-600">Invalid Invoice Data</p>
          <p className="text-sm sm:text-base text-gray-600 mt-2">The invoice data is incomplete or invalid</p>
        </div>
      </div>
    );
  }

  if (showPrintView) {
    return (
      <div>
        <div className="fixed top-4 right-4 z-50 space-x-4 print:hidden">
          <button
            onClick={() => {
              // Track actual print action
              if (invoice && user && id) {
                analytics.trackInvoiceDownload({
                  user,
                  invoiceId: id,
                  downloadType: 'print',
                  sessionId: getSessionId(),
                  metadata: {
                    action: 'browser_print',
                    student_name: `${invoice.enrollments.students.first_name} ${invoice.enrollments.students.last_name}`,
                    program_name: invoice.enrollments.programs.name,
                    invoice_status: invoice.status
                  }
                });
              }
              window.print();
            }}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            Print
          </button>
          <button
            onClick={() => setShowPrintView(false)}
            className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700"
          >
            Back
          </button>
        </div>
        <PrintableInvoice invoice={invoice as Invoice & { enrollments: EnrollmentWithDetails }} lineItems={lineItems} />
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-4">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center justify-between w-full">
          <button
            onClick={() => navigate('/payments')}
            className="text-blue-600 hover:text-blue-800 flex items-center space-x-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            <span>{t('action.backToPayments')}</span>
          </button>
          <button
            onClick={() => {
              if (invoice && user && id) {
                analytics.trackInvoiceDownload({
                  user,
                  invoiceId: id,
                  downloadType: 'print',
                  sessionId: getSessionId(),
                  metadata: {
                    student_name: `${invoice.enrollments.students.first_name} ${invoice.enrollments.students.last_name}`,
                    program_name: invoice.enrollments.programs.name,
                    invoice_status: invoice.status,
                    total_amount: invoice.total_amount,
                    balance_due: invoice.total_amount - invoice.amount_paid
                  }
                });
              }
              setShowPrintView(true);
            }}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 flex items-center space-x-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" 
                    d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
            </svg>
            <span>Printable Invoice</span>
          </button>
        </div>
      </div>
      <div className="space-y-6">
        <InvoiceDetailsBody
          invoice={invoice}
          lineItems={lineItems}
          isLineItemsLoading={isLineItemsLoading}
          onPayNow={() => navigate(checkoutPath(invoice.id, 'invoice_detail'))}
          onViewEnrollment={() => navigate(`/enrollments/${invoice.enrollment_id}`)}
          showWalletLink
        />
      </div>
    </div>
  );
} 